import { BookCampaign, BookState } from '@/types/state/book';
import { getTotalPrice, shouldShowFrom, showPrices } from './placeHelper';

export type SelectionPricing = {
  /** Formatted price the customer pays, post-campaign (e.g. "698 kr"). Empty string when prices are hidden. */
  total: string;
  /** Formatted pre-campaign price (e.g. "898 kr"), used as the struck-through original. */
  totalWithoutCampaigns: string;
  /** True when a campaign makes `total` differ from `totalWithoutCampaigns`. */
  hasCampaignPrice: boolean;
  /** True when the price is a variable ("from") price and a localized "from" prefix should be shown. */
  isPriceVariable: boolean;
  /** False when place/service settings hide prices entirely (price strings will be empty). */
  shouldShowPrices: boolean;
};

/**
 * Shared booking-flow pricing derivation used by the checkout selections bar.
 * Mirrors the SummaryView Footer logic in one place so the two surfaces agree:
 *   - total                 = getTotalPrice with campaigns applied
 *   - totalWithoutCampaigns = getTotalPrice with no campaigns (the original price)
 *   - hasCampaignPrice      = the two differ (a campaign is active)
 *
 * Both totals are summed over the SAME product basket: an undiscounted product
 * (one with no `originalPrice`) still costs its `price`, so the baseline falls
 * back to `price` instead of dropping the product — otherwise the struck-through
 * "original" can come out lower than what the customer actually pays.
 */
export function getSelectionPricing(
  pickedServices: BookState['services'] | null | undefined,
  place: BookState['place'],
  priceListId: string | number,
  campaigns: BookCampaign[] | null | undefined,
): SelectionPricing {
  const services = pickedServices ?? [];

  const pickedProducts = (campaigns ?? []).flatMap((c) => c.products ?? []);
  const productPrices = pickedProducts.map((p) => ({ price: p.price }));
  const productPricesWithoutCampaigns = pickedProducts.map((p) => ({ price: p.originalPrice ?? p.price }));

  const priceOf = (appliedCampaigns: BookCampaign[], products: { price: number }[], label = true) =>
    getTotalPrice(
      services,
      place,
      priceListId,
      {},
      0,
      1,
      label,
      appliedCampaigns,
      false,
      undefined,
      undefined,
      undefined,
      products,
    );

  const total = String(priceOf(campaigns ?? [], productPrices));
  const totalWithoutCampaigns = String(priceOf([], productPricesWithoutCampaigns));

  const hasCampaignPrice = Boolean(total && totalWithoutCampaigns && total !== totalWithoutCampaigns);
  const shouldShowPrices = showPrices(place, services);
  // A "from"/variable price gets the localized "from" prefix only when the post-campaign total is a real
  // payable amount. getTotalPrice(label=false) returns a number for real prices (0 when free) and a
  // non-number for the "varying"/invalid case — so a numeric check avoids comparing locale strings.
  const numericTotal = priceOf(campaigns ?? [], productPrices, false);
  const isPriceVariable =
    Boolean(shouldShowFrom(services, place)) && typeof numericTotal === 'number' && numericTotal > 0;

  return { total, totalWithoutCampaigns, hasCampaignPrice, isPriceVariable, shouldShowPrices };
}
